import { StatusMessageModalStatus } from '@news12/storybook-news'

import { useCallback, useState } from 'react'

import internalApiQueryWrapper from '../../core/internalApiQueryWrapper'
import { NewslettersList } from '../../data/query/fetchNewslettersList'
import { ModalName } from '../modal/types'
import useModal from '../modal/useModal'
import { validateEmail } from './utils/validateEmail'

const serializeSelectedNewsletters = ({
  newslettersList,
  email,
  regionName
}: {
  newslettersList: NewslettersList
  email: string
  regionName: string
}) => {
  const lists = newslettersList.filter(list => list.checked).reduce((acc, list) => ({ ...acc, [list.name]: 1 }), {})

  return {
    email,
    lists: JSON.stringify(lists),
    regionName
  }
}

export const NewsletterFieldErrorMessage = {
  REQUIRED: 'Oops, email is required!',
  INVALID: 'Oops, email address is invalid!',
  INTERNAL_ERROR: 'Oops, something went wrong'
}

type SubscriptionSuccess = {
  ok: true
}

const useNewsletter = (regionId: string, regionName: string) => {
  const [openNewsletterModal, closeNewsletterModal] = useModal(ModalName.NEWSLETTER_MODAL)
  const [openStatusMessageModal] = useModal(ModalName.STATUS_MESSAGE_MODAL)

  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const handleEmailChange = useCallback(({ target }) => setEmail(target.value), [])

  const handleSignUp = useCallback(
    async (newslettersList: NewslettersList) => {
      const { ok } = await internalApiQueryWrapper<SubscriptionSuccess>(
        '/api/newsletter/subscribe',
        serializeSelectedNewsletters({ newslettersList, email, regionName })
      )
      const statusModalConfig = ok
        ? {
            status: StatusMessageModalStatus.SUCCESS,
            title: 'All set, thanks for signing up!'
          }
        : {
            status: StatusMessageModalStatus.ERROR,
            title: 'Oops, something went wrong!'
          }

      closeNewsletterModal()
      openStatusMessageModal(statusModalConfig)
    },
    [email, openStatusMessageModal, closeNewsletterModal]
  )

  const loadNewsletterList = useCallback(async () => {
    return await internalApiQueryWrapper('/api/contentful/newsletter', {
      regionId
    })
  }, [regionId])

  const handleSubscribeClick = useCallback(async () => {
    if (!validateEmail(email)) {
      setErrorMessage(email ? NewsletterFieldErrorMessage.INVALID : NewsletterFieldErrorMessage.REQUIRED)
      return
    }

    const newsletter = await loadNewsletterList()

    if (!newsletter?.list?.length) {
      setErrorMessage(NewsletterFieldErrorMessage.INTERNAL_ERROR)
      return
    }

    setErrorMessage('')
    openNewsletterModal({
      title: 'Please select topics you want to be informed about',
      subscriptions: newsletter.list,
      onSubmit: handleSignUp
    })
  }, [email, openNewsletterModal, loadNewsletterList])

  return {
    errorMessage,
    email,
    handleEmailChange,
    handleSubscribeClick
  }
}

export default useNewsletter
